import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { getUnixTime } from 'date-fns/getUnixTime';
import { useTranslation } from 'react-i18next';

import { useProfileFuturesAppropriateness } from '@/hooks/queries/use-profile-futures-appropriateness';
import { useProfileNotificationMutation } from '@/hooks/queries/use-profile-notification';
import { useUserAttributes } from '@/hooks/queries/use-user-attributes';
import { FuturesAppropriatenessResultStatus } from '@/services/api/profile/futures-appropriateness';
import { NotificationKey } from '@/services/api/profile/notification';

export const FuturesAppropriatenessTestResultFailedButton = () => {
  const { isLoading: isLoadingUserAttributes, data: userAttributes } = useUserAttributes();
  const { isLoading: isLoadingFuturesAppropriateness, data: futuresAppropriateness } = useProfileFuturesAppropriateness();
  const { isPending, isSuccess, mutate } = useProfileNotificationMutation<NotificationKey.FUTURES_APPROPRIATENESS_TEST_RETAKE>();
  const { toast } = useToast();
  const { t } = useTranslation();
  const isLoading = isLoadingUserAttributes || isLoadingFuturesAppropriateness;
  const email = userAttributes?.email;

  if (isLoading) return null;

  if (!futuresAppropriateness || !('result' in futuresAppropriateness)) {
    throw new Error('Invalid futures appropriateness data');
  }

  const { result } = futuresAppropriateness;

  if (result.status !== FuturesAppropriatenessResultStatus.FAILED || result.canRetake) {
    throw new Error('Invalid futures appropriateness data');
  }

  if (!email) return null;

  const handleNotifyMe = () => {
    mutate(
      {
        key: NotificationKey.FUTURES_APPROPRIATENESS_TEST_RETAKE,
        body: {
          email,
          scheduledDate: getUnixTime(new Date(result.canRetakeFromDate)),
        },
      },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => {
          toast({
            description: t('FuturesAppropriatenessAssessment.result.failed.notifyMeSuccess.description'),
            title: t('FuturesAppropriatenessAssessment.result.failed.notifyMeSuccess.title'),
          });
        },
      },
    );
  };

  return (
    <Button className="relative" size="lg" variant="primary" disabled={isPending || isSuccess} onClick={handleNotifyMe}>
      <span className={isPending ? 'invisible' : ''}>{t('FuturesAppropriatenessAssessment.result.failed.actions.yesPleaseRemindMe')}</span>
      {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
    </Button>
  );
};
FuturesAppropriatenessTestResultFailedButton.displayName = 'FuturesAppropriatenessTestResultFailedButton';
